// src/components/GsiMap.js
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip as LeafletTooltip, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-polylinedecorator';
import './GsiMap.css';

// 🐛 Leafletのデフォルトアイコンバグ対策 ＆ 💡 一回り小さくリサイズ
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [18, 30],      /* 💡 元の [25, 41] からスマートに小型化 */
  iconAnchor: [9, 30],      /* 💡 アイコンの底面中央をピンポイントに合わせる [横/2, 縦] */
  popupAnchor: [0, -30],    /* 💡 ポップアップが出る位置を調整 */
  shadowSize: [30, 30],     /* 💡 影も合わせてリサイズ */
  shadowAnchor: [9, 30]
});
L.Marker.prototype.options.icon = DefaultIcon;

// 🔄 データが切り替わったときに、地図の中心を自動で追従させるコンポーネント
function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

// 🏹 軌跡の上に進行方向の矢印を重ねるコンポーネント（黒縁＋白のステルス型）
function PolylineArrows({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !positions || positions.length < 2) return;

    const polyline = L.polyline(positions);
    const decorator = L.polylineDecorator(polyline, {
      patterns: [
        {
          offset: '60px',
          repeat: '130px',
          symbol: L.Symbol.arrowHead({
            pixelSize: 10,
            headAngle: 35,
            polygon: true,
            pathOptions: { 
              stroke: true, 
              color: '#000000',
              weight: 1.5,
              fillColor: '#FFFFFF',
              fillOpacity: 1.0
            }
          })
        }
      ]
    });

    decorator.addTo(map);
    return () => { map.removeLayer(decorator); };
  }, [map, positions]);

  return null;
}

// 🖥️ 地図上に「全画面切り替えボタン」を配置するカスタムコントロール
function FullscreenButton({ isFullscreen, setIsFullscreen }) {
  const map = useMap();

  // 全画面切り替え時に地図のサイズ再計算を走らせる
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    setTimeout(() => {
      map.invalidateSize();
    }, 100);
  };

  return (
    <div className="leaflet-top leaflet-right"
        style={{ marginTop: '10px', marginRight: '14px', zIndex: 1000 }}>
      <div className="leaflet-control leaflet-bar" style={{ border: 'none', margin: 0 }}>
        <button
          onClick={toggleFullscreen}
          style={{
            backgroundColor: '#ffffff',
            border: 'none',
            width: '44px',
            height: '44px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            fontWeight: 'bold',
            color: '#333',
            borderRadius: '6px',
            boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
          }}
        >
          {isFullscreen ? '戻す' : '全画面'}
        </button>
      </div>
    </div>
  );
}

// 📏 2点間の距離（km）を求める Haversine
function distance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// 🧮 1km刻みのポイントデータをすべて生成（基準となる全データ）
function generateKmPoints(routePositions) {
  const kmPoints = [];
  let accumulated = 0;
  let nextKm = 1;

  for (let i = 1; i < routePositions.length; i++) {
    const [lat1, lon1] = routePositions[i - 1];
    const [lat2, lon2] = routePositions[i];

    const segment = distance(lat1, lon1, lat2, lon2);
    accumulated += segment;

    if (accumulated >= nextKm) {
      kmPoints.push({
        km: nextKm,
        lat: lat2,
        lon: lon2,
      });
      nextKm += 1;
    }
  }
  return kmPoints;
}

// 🔍 ズームレベルに応じて表示を自動切り替えるレイヤーコンポーネント
function KmTooltipLayer({ kmPoints }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // 描画と更新を行う関数
    const updateKmTooltips = () => {
      // 既存のツールチップをすべて削除
      if (map._kmTooltips) {
        map._kmTooltips.forEach(t => map.removeLayer(t));
      }

      const currentZoom = map.getZoom();
      // 閾値を14に設定：ズームが14未満（広域）なら5kmごと、14以上（詳細）なら1kmごと
      const thresholdZoom = 14;

      const activePoints = kmPoints.filter(p => {
        if (currentZoom < thresholdZoom) {
          return p.km % 5 === 0; // 5km刻み (5, 10, 15...)
        } else {
          return true; // 1km刻みですべて表示
        }
      });

      const tooltips = activePoints.map(p => {
        return L.tooltip({
          permanent: true,
          direction: 'top',
          offset: [0, 0],
          className: 'km-label'
        })
          .setLatLng([p.lat, p.lon])
          .setContent(`${p.km}`)
          .addTo(map);
      });

      map._kmTooltips = tooltips;
    };

    updateKmTooltips();

    // ズームが変わるたびに再描画
    map.on('zoomend', updateKmTooltips);

    return () => {
      map.off('zoomend', updateKmTooltips);
      if (map._kmTooltips) {
        map._kmTooltips.forEach(t => map.removeLayer(t));
        map._kmTooltips = null;
      }
    };
  }, [map, kmPoints]);

  return null;
}

function GsiMap({ 
  data, 
  gpxPoints,
  raceLimitTime 
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const routePositions = gpxPoints
    ? gpxPoints
        .filter(p => p && p.latitude !== undefined && p.longitude !== undefined && p.latitude !== null && p.longitude !== null)
        .map(p => [Number(p.latitude), Number(p.longitude)])
    : [];

  const hasRoute = routePositions.length > 0;

  const aidMarkers = gpxPoints
    ? gpxPoints.filter(p => p && p.区分 && p.区分 !== '' && p.latitude && p.longitude)
    : [];

  const defaultCenter = [35.9642, 139.2742];
  const mapCenter = hasRoute ? routePositions[0] : defaultCenter;

  const wrapperStyle = isFullscreen 
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        backgroundColor: '#ffffff',
        margin: 0
      }
    : {
        position: 'relative',
        width: '100%',
        height: '500px',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #ccc',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 1
      };

  const kmPoints = React.useMemo(() => {
    if (!hasRoute) return [];
    return generateKmPoints(routePositions);
  }, [routePositions, hasRoute]);

  return (
    <div className="gsi-map-wrapper" style={{ margin: isFullscreen ? 0 : '2rem 0', width: '100%' }}>
      
      <style>{`
        .apple-style-label {
          background-color: #ffffff !important;
          color: #000000 !important;
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif !important;
          font-size: 14px !important;  
          font-weight: 700 !important;
          letter-spacing: -0.1px !important;
          padding: 2px 5px !important;     
          border-radius: 4px !important;
          border: 1px solid rgba(0, 0, 0, 0.1) !important;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
        }
        .apple-style-label::before {
          border-top-color: #ffffff !important;
          filter: drop-shadow(0 2px 1px rgba(0, 0, 0, 0.1)) !important;
        }

        .leaflet-control-layers {
          border-radius: 8px !important;
          border: 1px solid rgba(0,0,0,0.15) !important;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
          font-family: -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 12px;
        }
        .leaflet-popup-content-wrapper {
          background: rgba(255, 255, 255, 0.75) !important; 
          backdrop-filter: blur(5px) !important;
          color: #333 !important;
          border-radius: 8px !important;
        }
        .leaflet-popup-tip {
          background: rgba(255, 255, 255, 0.75) !important;
        }
      `}</style>

      <div style={wrapperStyle}>
        <MapContainer 
          center={mapCenter} 
          zoom={12} 
          style={{ width: '100%', height: '100%' }}
        >
          <MapRecenter center={mapCenter} />

          <FullscreenButton isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />

          <LayersControl position="topright">
            <LayersControl.BaseLayer name="国土地理院">
              <TileLayer
                attribution='&copy; <a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank">国土地理院</a>'
                url="https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png"
                maxZoom={18}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer checked name="OpenTopoMap">
              <TileLayer
                attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                maxZoom={17}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="航空写真">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {hasRoute && (
            <Polyline 
              positions={routePositions} 
              color="#f50892" 
              weight={4} 
              opacity={0.8} 
            />
          )}

          {hasRoute && <PolylineArrows positions={routePositions} />}

          {aidMarkers.map((item, index) => {
            const matchTableItem = data 
              ? data.find(t => {
                  if (t.区分名称 && item.区分名称 && t.区分名称.trim() !== '' && item.区分名称.trim() !== '') {
                    if (t.区分名称 === item.区分名称) return true;
                  }
                  if (t.区分 && item.区分名称 && t.区分.trim() !== '' && item.区分名称.trim() !== '') {
                    if (t.区分 === item.区分名称) return true;
                  }
                  if (t.区分名称 && item.区分 && t.区分名称.trim() !== '' && item.区分.trim() !== '') {
                    if (t.区分名称 === item.区分) return true;
                  }
                  if (t.区分 && item.区分 && t.区分 === item.区分) {
                    if (item.区分 === 'FINISH' || item.区分 === 'START') {
                      return true;
                    }
                  }
                  return false;
                }) 
              : null;
            
            return (
              <Marker key={index} position={[item.latitude, item.longitude]}>
                <LeafletTooltip 
                  permanent 
                  direction="top" 
                  offset={[0, -28]} 
                  className="apple-style-label"
                >
                  {item.区分}
                </LeafletTooltip>

                <Popup maxWidth={220} minWidth={160}>
                  <div style={{ fontSize: '13px', lineHeight: '1.5', minWidth: '160px' }}>
                    <strong style={{ fontSize: '14px', color: '#333' }}>
                      {item.区分} {item.区分名称}
                    </strong>
                    <span style={{ color: '#666', marginLeft: '5px' }}>
                      ({item.cumulative_distance.toFixed(1)}km)
                    </span>
                    <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px solid #eee' }} />
                    
                    <b>到着時間:</b>{' '}
                    {matchTableItem && matchTableItem.到着時刻 ? (
                      (() => {
                        if (matchTableItem.到着時刻.includes('（')) {
                          const mainTime = matchTableItem.到着時刻.split('（')[0];
                          const restSub = matchTableItem.到着時刻.split('（')[1];
                          return (
                            <>
                              <span style={{ color: '#000000', fontWeight: 'bold' }}>{mainTime}</span>
                              <span style={{ color: '#0055ff', fontWeight: 'bold', fontSize: '11px', marginLeft: '4px' }}>
                                {`（${restSub}`}
                              </span>
                            </>
                          );
                        }

                        const name = (item.区分名称 || '').toUpperCase().trim();
                        const kbn = (item.区分 || '').toUpperCase().trim();
                        
                        const isStartOrFinish = 
                          name.includes('START') || name.includes('FINISH') || name.includes('FINSH') || name.includes('GOAL') || name.includes('スタート') || name.includes('ゴール') ||
                          kbn.includes('START') || kbn.includes('FINISH') || kbn.includes('FINSH') || kbn.includes('GOAL');

                        return (
                          <>
                            <span style={{ color: '#000000', fontWeight: 'bold' }}>{matchTableItem.到着時刻}</span>
                            {!isStartOrFinish && (
                              <span style={{ color: '#0055ff', fontWeight: 'bold', fontSize: '11px', marginLeft: '4px' }}>（Skip）</span>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      '-'
                    )}
                    <br />
                    
                    <b>標高:</b> {item.elevation !== null ? `${item.elevation.toFixed(1)}m` : '-'}<br />
                    
                    {matchTableItem && matchTableItem.weatherTemp ? (
                      <>
                        <b>予想気温:</b> {matchTableItem.weatherTemp}
                        {matchTableItem.weatherIconUrl && (
                          <img 
                            src={matchTableItem.weatherIconUrl} 
                            alt="weather" 
                            style={{ width: '20px', height: '20px', verticalAlign: 'middle', marginLeft: '4px' }} 
                          />
                        )}
                        <br />
                      </>
                    ) : (
                      item.weatherTemp && (
                        <>
                          <b>予想気温:</b> {item.weatherTemp}
                          {item.weatherIconUrl && (
                            <img 
                              src={item.weatherIconUrl} 
                              alt="weather" 
                              style={{ width: '20px', height: '20px', verticalAlign: 'middle', marginLeft: '4px' }} 
                            />
                          )}
                          <br />
                        </>
                      )
                    )}

                    {item.区分 === 'FINISH' && raceLimitTime && (
                      <div style={{ marginTop: '4px' }}>
                        <span style={{ color: '#e03131', fontWeight: 'bold' }}>
                          <b>制限時間:</b> {raceLimitTime}
                        </span>
                      </div>
                    )}                    

                    {item.gateTime && (
                      <span style={{ color: '#e03131', fontWeight: 'bold' }}>
                        <b>関門時間:</b> {item.gateTime}
                      </span>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* 🔍 ズームに応じて5km/1kmを自動切り替える距離マーカー */}
          <KmTooltipLayer kmPoints={kmPoints} />
        </MapContainer>
      </div>
    </div>
  );
}

export default GsiMap;